import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { getWhatsAppUrl } from "./WhatsAppButton";
import { BubbleBackground } from "./BubbleBackground";
import whatsappLogo from "@/assets/whatsapp-logo.svg";
import professionalCleaning from "@/assets/professional-cleaning.webp";
import aon from "@/assets/logos/aon.webp";
import daytonaRally from "@/assets/logos/daytona-rally.webp";
import growthpoint from "@/assets/logos/growthpoint.webp";
import loreal from "@/assets/logos/loreal.webp";
import pepsico from "@/assets/logos/pepsico.webp";
import ubs from "@/assets/logos/ubs.webp";

const logos = [
  { src: aon, alt: "AON", scale: 1 },
  { src: daytonaRally, alt: "Daytona Rally", scale: 1 },
  { src: growthpoint, alt: "Growthpoint", scale: 1.5 },
  { src: loreal, alt: "L'Oréal", scale: 1 },
  { src: pepsico, alt: "PepsiCo", scale: 1 },
  { src: ubs, alt: "UBS", scale: 1 },
];



export const HeroSection = () => {
  const scrollToForm = () => {
    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[100svh] flex flex-col justify-between">
      {/* Background Image with Premium Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={professionalCleaning}
          alt="Professional corporate car wash service"
          className="w-full h-full object-cover"
          width={2000}
          height={1287}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 hero-gradient" />
      </div>

      {/* Luxurious Bubbles */}
      <BubbleBackground variant="dark" density="normal" />

      {/* Content */}
      <div className="container relative z-10 pt-28 md:pt-36 pb-6 flex-1 flex items-center">
        <div className="max-w-3xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm text-primary-foreground/80 text-xs uppercase tracking-[0.2em] mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Premium On-Site Detailing • Gauteng
          </span>

          <h1 className="heading-display text-primary-foreground mb-6">
            Spotless Corporate Fleets,
            <br />
            <span className="text-accent">Without Leaving Your Premises</span>
          </h1>

          <p
            className="text-lg md:text-2xl text-primary-foreground/75 mb-6 md:mb-10 leading-relaxed max-w-2xl mx-auto font-light"
          >
            Premium on-site car washing for executive, company and staff
            vehicles. Discreet, scheduled, and finished to a showroom standard.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-10 justify-center"
          >
            <Button variant="cta" size="xl" onClick={scrollToForm}>
              Request a Corporate Quote
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button variant="outlineLight" size="xl" asChild>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener">
                <img src={whatsappLogo} alt="WhatsApp" className="h-5 w-5" />
                WhatsApp Us
              </a>
            </Button>
          </div>

          {/* Trust Line */}
          <p className="text-primary-foreground/55 text-sm">
            Trusted by leading Gauteng businesses • 5+ years on-site experience
          </p>
        </div>
      </div>

      {/* Logo scroll at bottom of hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="relative z-10 pb-6 md:pb-8"
      >
        <p className="text-center text-primary-foreground/80 text-xs uppercase tracking-widest mb-4">
          Trusted by leading businesses
        </p>
        <div className="relative w-full overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[hsl(215_50%_8%/0.95)] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[hsl(215_50%_8%/0.95)] to-transparent z-10 pointer-events-none" />

          <div className="flex w-max animate-logo-marquee">
            {[0, 1].map((trackIndex) => (
              <div
                key={trackIndex}
                className="flex items-center gap-12 md:gap-16 pr-12 md:pr-16 shrink-0"
                aria-hidden={trackIndex === 1}
              >
                {logos.map((logo) => (
                  <div
                    key={`${logo.alt}-${trackIndex}`}
                    className="flex-shrink-0 h-14 md:h-16 w-32 md:w-40 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/10 hover:bg-white/20 transition-colors duration-300"
                  >
                    <img
                      src={logo.src}
                      alt={`${logo.alt} corporate client logo`}
                      className="max-h-full max-w-full object-contain brightness-0 invert opacity-70 hover:opacity-100 transition-opacity duration-300"
                      style={{ transform: `scale(${logo.scale})` }}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};
